import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function FondueInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "fondue-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "where-to-buy": "Where to Buy Ingredients",
    "best-fondue-places": "Best Fondue Places in Vancouver",
    "hosting-tips": "Hosting a Fondue Night at Home",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // (All references are representative or extracted from the conversation.)
  const referencesData = [
    {
      url: "https://granvilleisland.com",
      label: "Granville Island Public Market – Official Website",
    },
    {
      url: "https://inspection.canada.ca",
      label: "Canadian Food Inspection Agency – Import Guidelines",
    },
    {
      url: "https://vancouver.ca",
      label: "City of Vancouver – Zero Waste Centre",
    },
    {
      text: "Les Amis du Fromage – Fondue FAQ & Cheese Selection Guidance.",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" must match the ref you use in the corresponding
  // text section. The lat/lng come from the conversation. 
  const locations = [
    {
      id: "amis-kits",
      lat: 49.27080,
      lng: -123.14000,
      popupText: "Les Amis du Fromage (Kitsilano)",
    },
    {
      id: "amis-east",
      lat: 49.28105,
      lng: -123.07629,
      popupText: "Les Amis du Fromage (East Van)",
    },
    {
      id: "granville-market",
      lat: 49.27236,
      lng: -123.13507,
      popupText: "Granville Island Public Market",
    },
    {
      id: "gourmet-warehouse",
      lat: 49.28105,
      lng: -123.07629,
      popupText: "The Gourmet Warehouse",
    },
    {
      id: "whole-foods-cambie",
      lat: 49.26350,
      lng: -123.11500,
      popupText: "Whole Foods Market (Cambie)",
    },
    {
      id: "vegan-supply",
      lat: 49.28050,
      lng: -123.10000,
      popupText: "Vegan Supply (Chinatown)",
    },
    {
      id: "french-table",
      lat: 49.24950,
      lng: -123.10100,
      popupText: "The French Table",
    },
    {
      id: "inevitable-fondue",
      lat: 49.26450,
      lng: -123.16100,
      popupText: "Inevitable Fondue & Piano Lounge",
    },
    {
      id: "burgoo-mt-pleasant",
      lat: 49.25680,
      lng: -123.10000,
      popupText: "Burgoo Bistro (Mount Pleasant)",
    },
    {
      id: "mink-chocolate",
      lat: 49.28610,
      lng: -123.11650,
      popupText: "Mink – A Chocolate Café",
    },
    {
      id: "capstone-robson",
      lat: 49.28900,
      lng: -123.13250,
      popupText: "Capstone Tea & Fondue (Robson)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Enjoy a Homemade Fondue in Vancouver</title>
        <meta
          name="description"
          content="A complete guide to making and enjoying fondue in Vancouver – from where to buy ingredients to top fondue restaurants, hosting tips, and local regulations."
        />
        <meta
          name="keywords"
          content="fondue, vancouver, cheese, dining, recipes, vegan, restaurants"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/fondue-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Enjoy a Homemade Fondue in Vancouver"
        />
        <meta
          property="og:description"
          content="Explore the best spots to buy fondue ingredients, top fondue restaurants, and how to host a cozy fondue night at home in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/fondue-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/fondue-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Enjoy a Homemade Fondue in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Where to find top-quality ingredients, must-try fondue restaurants, and practical tips for a memorable fondue night."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/fondue-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A fondue pot filled with melted cheese and bread cubes."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a city that loves good food, and fondue fits right in
              with its cozy, communal dining culture. Whether you’re dipping
              fresh bread into aromatic melted cheese or savoring rich chocolate
              with fruit, fondue is an interactive experience ideal for chilly
              nights and friendly gatherings. In this guide, we’ll explore the
              best places to buy fondue ingredients, recommend local restaurants
              that serve stellar fondue, offer step-by-step tips for hosting a
              fondue party at home, and cover the local regulations to help you
              stay safe and informed.
            </p>

            {/* SECTION: Where to Buy Ingredients */}
            <h2 id="where-to-buy">Where to Buy Ingredients</h2>
            <p>
              Finding top-quality cheese, chocolate, and other fondue essentials
              in Vancouver is easier than you might think. Here are some
              go-to shops to set you up for fondue success:
            </p>

            <p
              ref={(el) => setSpotRef("amis-kits", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Les Amis du Fromage (Kitsilano)</strong>{" "}
              <Marker
                coordY={49.27080}
                coordX={-123.14000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1752 West 2nd Avenue, Vancouver. A family-run cheese shop
              offering a wide selection of imported Swiss cheeses (Emmental,
              Gruyère, Appenzeller) and excellent local BC options.
              They’re open daily, roughly 10am–6pm.
            </p>

            <p
              ref={(el) => setSpotRef("amis-east", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Les Amis du Fromage (East Vancouver)</strong>{" "}
              <Marker
                coordY={49.28105}
                coordX={-123.07629}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 843 East Hastings Street, Vancouver. The same great selection of
              cheeses and charcuterie in the Hastings-Sunrise area. Perfect for
              picking up fondue-ready cheeses and specialty items.
            </p>

            <p
              ref={(el) => setSpotRef("granville-market", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Granville Island Public Market</strong>{" "}
              <Marker
                coordY={49.27236}
                coordX={-123.13507}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1689 Johnston Street, Vancouver. Discover artisanal cheese,
              breads, charcuterie, and fresh produce all under one roof. Perfect
              for constructing a well-rounded fondue spread.
            </p>

            <p
              ref={(el) => setSpotRef("gourmet-warehouse", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Gourmet Warehouse</strong>{" "}
              <Marker
                coordY={49.28105}
                coordX={-123.07629}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1340 East Hastings Street, Vancouver. A large emporium for all
              things gourmet, including fondue pots, Swiss chocolates, cooking
              oils, and more.
            </p>

            <p
              ref={(el) => setSpotRef("whole-foods-cambie", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Whole Foods Market (Cambie)</strong>{" "}
              <Marker
                coordY={49.26350}
                coordX={-123.11500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 510 West 8th Avenue, Vancouver. Expect a variety of organic
              cheeses, breads, vegan cheese alternatives, and gluten-free
              options. Open daily, 7am–10pm.
            </p>

            <p
              ref={(el) => setSpotRef("vegan-supply", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vegan Supply (Chinatown)</strong>{" "}
              <Marker
                coordY={49.28050}
                coordX={-123.10000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 250 East Pender Street, Vancouver. For plant-based fondue, this
              store has an impressive selection of vegan cheeses, chocolates,
              and meat alternatives.
            </p>

            {/* SECTION: Best Fondue Places in Vancouver */}
            <h2 id="best-fondue-places">Best Fondue Places in Vancouver</h2>
            <p>
              Not in the mood to DIY? Several local restaurants specialize in
              fondue, ranging from classic Swiss styles to dessert fondues:
            </p>

            <p
              ref={(el) => setSpotRef("french-table", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The French Table</strong>{" "}
              <Marker
                coordY={49.24950}
                coordX={-123.10100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 3916 Main Street, Vancouver. Offers a traditional Swiss-style
              cheese fondue with Emmental, Gruyère, and Appenzeller, plus beef
              fondue in a Pinot Noir broth.
            </p>

            <p
              ref={(el) => setSpotRef("inevitable-fondue", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Inevitable Fondue &amp; Piano Lounge</strong>{" "}
              <Marker
                coordY={49.26450}
                coordX={-123.16100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 2649 West Broadway, Vancouver. A fondue-centric eatery with a
              fun live piano vibe. Offers cheese, broth-based meat, and
              chocolate fondues.
            </p>

            <p
              ref={(el) => setSpotRef("burgoo-mt-pleasant", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Burgoo Bistro (Mount Pleasant)</strong>{" "}
              <Marker
                coordY={49.25680}
                coordX={-123.10000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 3096 Main Street, Vancouver. Known for comforting cheese
              fondue served with bread, apples, and a cozy pub-like setting.
            </p>

            <p
              ref={(el) => setSpotRef("mink-chocolate", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Mink &ndash; A Chocolate Café</strong>{" "}
              <Marker
                coordY={49.28610}
                coordX={-123.11650}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 863 West Hastings Street, Vancouver. Specializes in dessert
              fondue featuring gourmet melted chocolate with fruit and cake for
              dipping.
            </p>

            <p
              ref={(el) => setSpotRef("capstone-robson", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Capstone Tea &amp; Fondue (Robson)</strong>{" "}
              <Marker
                coordY={49.28900}
                coordX={-123.13250}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1429 Robson Street, Vancouver. Formerly known for its
              all-you-can-eat chocolate fondue and afternoon tea. Currently
              closed at this location but worth mentioning for its fondue legacy.
            </p>

            {/* SECTION: Hosting a Fondue Night at Home */}
            <h2 id="hosting-tips">Hosting a Fondue Night at Home</h2>
            <p>
              There’s nothing like a cozy fondue party. Set up a fondue pot (or
              two), gather some friends, and enjoy a fun, interactive meal.
              Here’s a quick how-to:
            </p>
            <ul>
              <li>
                <strong>Equipment:</strong> Choose a ceramic fondue pot (for
                cheese/chocolate) or a metal one (for oil/broth). Don’t forget
                long-handled fondue forks.
              </li>
              <li>
                <strong>Cheese Fondue:</strong> A classic blend includes
                Gruyère, Emmental, and Appenzeller with white wine and a splash
                of kirsch.
              </li>
              <li>
                <strong>Chocolate Fondue:</strong> Use high-quality dark or milk
                chocolate, add cream, and serve with fruits, marshmallows,
                cookies, or cake.
              </li>
              <li>
                <strong>Meat Fondue:</strong> Heat oil or broth to cook beef,
                chicken, or seafood. Provide sauces for dipping.
              </li>
              <li>
                <strong>Safety &amp; Clean-Up:</strong> Keep raw meats chilled
                until use. Dispose of used oil properly (never down the drain).
              </li>
              <li>
                <strong>Ambiance:</strong> Dim the lights, turn on some mellow
                tunes, and enjoy leisurely conversation over the fondue pot.
              </li>
            </ul>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Proper handling of cheese and meats is essential for food safety.
              Vancouver Coastal Health advises keeping hot foods hot (above
              60°C) and raw meats separate from ready-to-eat foods. Cooking oil
              should be recycled at the Zero Waste Centre rather than poured
              down the drain. If you plan to import special cheeses or meats,
              check the Canadian Food Inspection Agency guidelines on personal
              import limits. Lastly, exercise caution when using open-flame
              burners indoors—keep them away from flammable items and out of
              children’s reach.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What cheese mix is best for fondue?</h3>
              <p>
                Many swear by the Swiss trio of Gruyère, Emmental, and
                Appenzeller. Mix them with dry white wine, a little cornstarch,
                and some kirsch for a smooth, tangy fondue.
              </p>

              <h3>2. Are there vegan fondue options available?</h3>
              <p>
                Absolutely. Vegan cheeses (e.g., cashew-based) melt well in fondue.
                Use dairy-free chocolate for dessert fondues. Vegan Supply in
                Chinatown stocks many plant-based options.
              </p>

              <h3>3. Where can I find affordable fondue sets?</h3>
              <p>
                The Gourmet Warehouse often has reasonably priced sets. You can
                also check stores like Canadian Tire or look for second-hand
                deals online.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From picking up quality cheeses at local shops to indulging in a
              full-service fondue dinner at a Vancouver restaurant, there are
              plenty of ways to enjoy this fun, communal meal. Host a party with
              friends, follow local safety guidelines, and savor the city’s
              abundance of fondue-friendly ingredients and dining spots. Whether
              you opt for a homemade or restaurant experience, fondue in
              Vancouver is sure to warm your heart (and taste buds).
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}